import { useEffect, useRef, useState } from "react"

// Animated mini-mockup for the Pipeline feature row.
// Loop: list view → toggle to board → Westbrook card slides Proposal → Submitted.
// All styles live under `.pl-*` classes in src/index.css (scoped to .landing-page).

type Bid = {
  title: string
  client: string
  value: string
  due: string
  status: "lead" | "proposal" | "submitted" | "won"
  initials: string
  avatar: "a1" | "a2" | "a3" | "a4" | "a5"
}

const BIDS: [Bid, Bid, Bid, Bid, Bid] = [
  { title: "Westbrook Highway Upgrade",  client: "Cohen & Stark",         value: "$4.2M",  due: "12 Jun", status: "proposal",  initials: "KK", avatar: "a1" },
  { title: "Meridian Defense Systems",   client: "DoD Acquisition Office", value: "$11.8M", due: "28 Jun", status: "submitted", initials: "JT", avatar: "a4" },
  { title: "Northwind Rail Modernisation", client: "Transport NSW",        value: "$22.4M", due: "04 Aug", status: "lead",      initials: "NM", avatar: "a2" },
  { title: "Cobalt Energy Retrofit",     client: "Cobalt Utilities",       value: "$6.7M",  due: "19 May", status: "won",       initials: "EC", avatar: "a5" },
  { title: "Harborline Logistics",       client: "Port of Adelaide",       value: "$3.1M",  due: "07 Jul", status: "proposal",  initials: "SH", avatar: "a3" },
]

const STATUS_LABEL: Record<Bid["status"], string> = {
  lead: "Lead",
  proposal: "Proposal",
  submitted: "Submitted",
  won: "Won",
}

export function PipelineVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? false),
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={rootRef} className={`pl-frame ${inView ? "pl-playing" : ""}`}>
      {/* ── chrome ── */}
      <div className="pl-chrome">
        <div className="pl-chrome-search">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="7" cy="7" r="4.5" />
            <line x1="10.5" y1="10.5" x2="14" y2="14" />
          </svg>
          <span>Search bids…</span>
          <span className="pl-kbd">⌘K</span>
        </div>
        <div className="pl-chrome-actions">
          <button type="button" className="pl-icon-btn" aria-label="Filter">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 4h12M4 8h8M6 12h4" />
            </svg>
          </button>
          <button type="button" className="pl-icon-btn" aria-label="Export">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10" />
            </svg>
          </button>
          <div className="pl-toggle-wrap">
            <button type="button" className="pl-toggle-btn pl-is-list" aria-label="List view">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="3" y1="4.5" x2="13" y2="4.5" />
                <line x1="3" y1="8" x2="13" y2="8" />
                <line x1="3" y1="11.5" x2="13" y2="11.5" />
              </svg>
            </button>
            <button type="button" className="pl-toggle-btn pl-is-board" aria-label="Board view">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2.5" y="2.5" width="5" height="11" rx="0.5" />
                <rect x="8.5" y="2.5" width="5" height="11" rx="0.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── stage ── */}
      <div className="pl-stage">
        {/* List view */}
        <div className="pl-view pl-view-list">
          <div className="pl-list-header">
            <div>Bid title</div>
            <div>Client</div>
            <div>Value</div>
            <div>Deadline</div>
            <div>Status</div>
          </div>
          {BIDS.map((b) => (
            <div key={b.title} className="pl-list-row">
              <div className="pl-name">{b.title}</div>
              <div className="pl-client">{b.client}</div>
              <div className="pl-value">{b.value}</div>
              <div className="pl-due">{b.due}</div>
              <div>
                <span className={`pl-pill pl-${b.status}`}>{STATUS_LABEL[b.status]}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Board view */}
        <div className="pl-view pl-view-board">
          <div className="pl-board">
            {/* Lead */}
            <div className="pl-col">
              <div className="pl-col-head">
                <span className="pl-col-title pl-lead"><span className="pl-dot" />Lead</span>
                <span className="pl-col-count">1</span>
              </div>
              <BoardCard bid={BIDS[2]} />
            </div>

            {/* Proposal */}
            <div className="pl-col">
              <div className="pl-col-head">
                <span className="pl-col-title pl-proposal"><span className="pl-dot" />Proposal</span>
                <span className="pl-col-count pl-count-proposal" />
              </div>
              <BoardCard bid={BIDS[0]} extraClass="pl-card-westbrook-from" />
              <BoardCard bid={BIDS[4]} />
            </div>

            {/* Submitted */}
            <div className="pl-col">
              <div className="pl-col-head">
                <span className="pl-col-title pl-submitted"><span className="pl-dot" />Submitted</span>
                <span className="pl-col-count pl-count-submitted" />
              </div>
              <BoardCard bid={BIDS[1]} />
              <BoardCard bid={BIDS[0]} extraClass="pl-card-westbrook-to" />
            </div>

            {/* Won */}
            <div className="pl-col">
              <div className="pl-col-head">
                <span className="pl-col-title pl-won"><span className="pl-dot" />Won</span>
                <span className="pl-col-count">1</span>
              </div>
              <BoardCard bid={BIDS[3]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function BoardCard({ bid, extraClass }: { bid: Bid; extraClass?: string }) {
  return (
    <div className={`pl-card${extraClass ? ` ${extraClass}` : ""}`}>
      <div className="pl-card-name">{bid.title}</div>
      <div className="pl-card-client">{bid.client}</div>
      <div className="pl-card-meta">
        <span className="pl-card-value">{bid.value}</span>
        <span className={`pl-avatar pl-${bid.avatar}`}>{bid.initials}</span>
      </div>
    </div>
  )
}
